// Shared presentation helpers for subagent ("task") surfaces: the inline
// parallel-agents widget in the chat feed (message-part.tsx) and the app's
// agents side panel / composer pill. Kept in its own module so the app can
// import it without pulling the whole message-part bundle, and so the agent
// color assignment stays identical everywhere a subagent is shown.

import type { Message } from "@opencode-ai/sdk/v2"

// Stable tones for the built-in agents; everything else hashes into the
// palette below so a given agent name always renders in the same color.
export const agentTones: Record<string, string> = {
  ask: "var(--icon-agent-ask-base)",
  build: "var(--icon-agent-build-base)",
  docs: "var(--icon-agent-docs-base)",
  plan: "var(--icon-agent-plan-base)",
}

export const agentPalette = [
  "var(--icon-agent-ask-base)",
  "var(--icon-agent-build-base)",
  "var(--icon-agent-docs-base)",
  "var(--icon-agent-plan-base)",
  "var(--syntax-info)",
  "var(--syntax-success)",
  "var(--syntax-warning)",
  "var(--syntax-property)",
  "var(--syntax-constant)",
  "var(--text-diff-add-base)",
  "var(--text-diff-delete-base)",
  "var(--icon-warning-base)",
]

export function tone(name: string) {
  let hash = 0
  for (const char of name) hash = (hash * 31 + char.charCodeAt(0)) >>> 0
  return agentPalette[hash % agentPalette.length]
}

export function taskAgent(
  raw: unknown,
  list?: readonly { name: string; color?: string }[],
): { name?: string; color?: string } {
  if (typeof raw !== "string" || !raw) return {}
  const key = raw.toLowerCase()
  const item = list?.find((entry) => entry.name === raw || entry.name.toLowerCase() === key)
  return {
    name: item?.name ?? `${raw[0]!.toUpperCase()}${raw.slice(1)}`,
    color: item?.color ?? agentTones[key] ?? tone(key),
  }
}

/** "113.3k" / "1.1M" — matches how agent token spend is shown everywhere. */
export function formatTokenCount(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return "0"
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}k`
  return `${Math.round(value)}`
}

/** "$0.42" / "<$0.01" / "$12.30" — USD as the providers report it. Callers hide
 * a zero (unknown or free) rather than print "$0.00". */
export function formatCost(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return ""
  if (value < 0.01) return "<$0.01"
  if (value >= 100) return `$${Math.round(value)}`
  return `$${value.toFixed(2)}`
}

/** Cumulative token spend and cost across a session's assistant messages —
 * the "how much did this agent burn" number, cache reads included (they are
 * billed input). Returns zeros when the messages are not loaded. */
export function sessionSpend(messages: readonly Message[] | undefined): { tokens: number; cost: number } {
  let tokens = 0
  let cost = 0
  for (const message of messages ?? []) {
    if (message.role !== "assistant") continue
    const t = message.tokens
    if (t) tokens += t.input + t.output + t.reasoning + t.cache.read + t.cache.write
    cost += message.cost ?? 0
  }
  return { tokens, cost }
}

/** Session titles created by the task tool look like
 * "Fix the parser (@Explore subagent)" — strip the suffix for display. */
export function taskTitle(title: string | undefined): string {
  if (!title) return ""
  return title.replace(/\s*\(@[^)]+ subagent\)\s*$/, "")
}

/** The agent name embedded in a task-created session title, as a fallback for
 * child sessions whose record carries no `agent` field (the task tool does not
 * set one at create time). */
export function taskTitleAgent(title: string | undefined): string | undefined {
  return title?.match(/\(@([^)]+) subagent\)\s*$/)?.[1]
}

/** Elapsed duration label: "12s", "4m 05s", "1h 12m". */
export function formatElapsed(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return ""
  const total = Math.floor(ms / 1000)
  if (total < 60) return `${total}s`
  const minutes = Math.floor(total / 60)
  const seconds = total % 60
  if (minutes < 60) return `${minutes}m ${String(seconds).padStart(2, "0")}s`
  const hours = Math.floor(minutes / 60)
  return `${hours}h ${String(minutes % 60).padStart(2, "0")}m`
}
